import React, { useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useAuth, useMounted } from 'hooks';
import { useCollectionById, useSubscribe, useSubscriptions, useUnsubscribe } from 'queries';
import { FormattedMessage, useIntl } from 'react-intl';

import { mapGameDetails } from 'utils/gameDetails/game.utils';

import { GameSubscription, SubscriptionType } from 'services/api/definitions/game.definitions';

import GradientTextButton from 'components/@common/Button/GradientTextButton';
import ConfirmationDialog from 'components/@common/ConfirmationDialog';
import About from 'components/@game-detail/About';
import NotLoggedInCta from 'components/@home/NotLoggedInCta';

interface Props {
  subscription: GameSubscription;
  collectionId: string;
}

const SubscriptionDetail = ({ subscription, collectionId }: Props) => {
  const intl = useIntl();
  const isMounted = useMounted();
  const [subscribeState, setSubscribeState] = useState<'subscribe' | 'unsubscribe'>();

  const { collection } = useCollectionById(collectionId);

  const { isLoggedIn } = useAuth();
  const { isSubscribedTo, isLoading: subscriptionsIsLoading } = useSubscriptions();
  const { unsubscribe, isLoading: unsubscribeIsLoading } = useUnsubscribe();
  const { subscribe, isLoading: subscribeIsLoading } = useSubscribe();

  const handleSubscribe = () => {
    subscribe(subscription.subscribeUrl);
    setSubscribeState(undefined);
  };

  const handleUnsubscribe = () => {
    unsubscribe(subscription.unsubscribeUrl);
    setSubscribeState(undefined);
  };

  const renderButtons = () => {
    if (subscriptionsIsLoading || !isMounted) {
      return <Skeleton width="100%" height={42} variant="rect" />;
    } else if (isSubscribedTo(subscription.shortCode)) {
      return (
        <>
          <Button
            onClick={() => setSubscribeState('unsubscribe')}
            variant="outlined"
            color="primary"
            fullWidth
            disabled={unsubscribeIsLoading || !isLoggedIn}
          >
            {unsubscribeIsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FormattedMessage id="subscription.details.actions.deactivate" />
            )}
          </Button>
          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage
                id={
                  // TODO: check all possibilities
                  subscription.subscriptionType === SubscriptionType.oneTimePurchase
                    ? 'game.explainer_text.one_time_pay'
                    : 'game.explainer_text.per_game_session'
                }
              />
            </Typography>
          </Box>
        </>
      );
    } else {
      return (
        <>
          <Button
            onClick={() => setSubscribeState('subscribe')}
            variant="contained"
            color="primary"
            fullWidth
            disabled={subscribeIsLoading || !isLoggedIn}
          >
            {subscribeIsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FormattedMessage
                id="subscription.details.actions.activate"
                values={{
                  amount:
                    subscription.price.toLowerCase() === 'free'
                      ? intl.formatMessage({ id: 'game.details.price.free' })
                      : subscription.price,
                }}
              />
            )}
          </Button>
          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage
                id={
                  // TODO: check all possibilities
                  subscription.subscriptionType === SubscriptionType.oneTimePurchase
                    ? 'game.explainer_text.one_time_pay'
                    : 'game.explainer_text.per_game_session'
                }
              />
            </Typography>
          </Box>
        </>
      );
    }
  };
  return (
    <>
      <Box display="flex" flexDirection="column" mb={2}>
        <Box mx={4} mb={2}>
          <Box mb={6}>
            {renderButtons()}
            <NotLoggedInCta
              title={intl.formatMessage({ id: 'games.login.title' })}
              description={intl.formatMessage({ id: 'games.login.body' })}
              titleVariant="h6"
              bgcolor="background.paper"
              p={4}
              borderRadius={4}
              my={6}
            />
          </Box>
          <Typography variant="h6" component="p">
            <FormattedMessage id="game.details.tabs.about" />
          </Typography>
        </Box>
        <About recommendedGames={collection.slice(0, 3)} details={mapGameDetails(subscription)} />
      </Box>
      <ConfirmationDialog
        isOpen={!!subscribeState}
        title={intl.formatMessage({
          id: `subscription.details.dialog.${subscribeState ?? 'subscribe'}.title`,
        })}
        description={intl.formatMessage(
          {
            id: `subscription.details.dialog.${subscribeState ?? 'subscribe'}.body`,
          },
          {
            amount:
              subscription.price.toLowerCase() === 'free'
                ? intl.formatMessage({ id: 'game.details.price.free' })
                : subscription.price,
          },
        )}
        onClose={() => setSubscribeState(undefined)}
        secondaryButton={
          <Button
            color="primary"
            onClick={() => setSubscribeState(undefined)}
            disabled={unsubscribeIsLoading}
            size="medium"
          >
            <FormattedMessage id="general.button.cancel" />
          </Button>
        }
        primaryButton={
          subscribeState === 'subscribe' ? (
            <GradientTextButton
              disabled={unsubscribeIsLoading || subscribeIsLoading}
              onClick={handleSubscribe}
              size="medium"
            >
              {unsubscribeIsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage
                  id={`subscription.details.dialog.${subscribeState ?? 'subscribe'}.button`}
                />
              )}
            </GradientTextButton>
          ) : (
            <Button
              color="secondary"
              disabled={unsubscribeIsLoading || subscribeIsLoading}
              onClick={handleUnsubscribe}
              size="medium"
            >
              {unsubscribeIsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage
                  id={`subscription.details.dialog.${subscribeState ?? 'subscribe'}.button`}
                />
              )}
            </Button>
          )
        }
      />
    </>
  );
};

export default SubscriptionDetail;
